import {
    ColumnWidth,
    ResizableColumnRegistration,
    ResizeStrategy,
    ToggleMeasureBranch,
} from "../../state/resizable-column-controller.context";

export interface ParsedWidth {
    fixedPx?: number;
    starWeight?: number;
}

const EPSILON = 0.0001;
const SAFETY_LIMIT = 250;

export function parseWidth(width: ColumnWidth): ParsedWidth {
    if (typeof width === "number" && Number.isFinite(width)) {
        return { fixedPx: Math.max(0, width) };
    }

    const text = `${width || ""}`.trim();
    const match = text.match(/^(\d+(?:\.\d+)?)\*$/);
    if (match) {
        const weight = Number.parseFloat(match[1]);
        if (Number.isFinite(weight) && weight > 0) {
            return { starWeight: weight };
        }
    }
    return { starWeight: 1 };
}

export function clampNumber(value: number, min?: number, max?: number): number {
    const lower = Number.isFinite(min) ? (min as number) : Number.NEGATIVE_INFINITY;
    const upper = Number.isFinite(max) ? (max as number) : Number.POSITIVE_INFINITY;
    return Math.min(Math.max(value, lower), upper);
}

interface NormalizedBounds {
    min: number;
    max: number;
}

function normalizeBounds(column: ResizableColumnRegistration): NormalizedBounds {
    const min = Math.max(0, Number.isFinite(column.minWidth) ? (column.minWidth as number) : 0);
    const rawMax = Number.isFinite(column.maxWidth) ? (column.maxWidth as number) : Number.POSITIVE_INFINITY;
    return {
        min,
        max: Math.max(min, rawMax),
    };
}

function sum(values: number[]): number {
    return values.reduce((total, value) => total + (Number.isFinite(value) ? value : 0), 0);
}

interface DistributionCandidate {
    index: number;
    weight: number;
    room: number;
}

/**
 * Tier-aware, weight-proportional residual distribution.
 *
 * - Tier 1: star (relative-width) columns within their min/max bounds.
 * - Tier 2: fixed-width columns within their bounds, used only if no star
 *   column has room. Weighted by current width as a sensible proxy.
 * - Tier 3 (grow only): when every eligible column is saturated at maxWidth,
 *   allow breaking maxWidth weighted by star weight so the container is
 *   filled (REQ-001 / tie-break clarification: maxWidth is the lowest
 *   priority constraint).
 *
 * Shrink direction never crosses minWidth (REQ-002 — minWidth is inviolate).
 */
function distributeResidual(
    initialWidths: number[],
    columns: ResizableColumnRegistration[],
    targetTotal: number,
    eligibleIndexes: Set<number>,
): number[] {
    const widths = initialWidths.map((value) => (Number.isFinite(value) ? Math.max(0, value) : 0));
    const bounds = columns.map((column) => normalizeBounds(column));
    const parsed = columns.map((column) => parseWidth(column.width));

    for (let index = 0; index < widths.length; index++) {
        widths[index] = clampNumber(widths[index], bounds[index].min, bounds[index].max);
    }

    let residual = targetTotal - sum(widths);
    let safety = 0;

    while (Math.abs(residual) > EPSILON && safety < SAFETY_LIMIT) {
        safety++;
        const grow = residual > 0;

        const tier1: DistributionCandidate[] = [];
        const tier2: DistributionCandidate[] = [];
        for (let index = 0; index < widths.length; index++) {
            if (!eligibleIndexes.has(index)) {
                continue;
            }
            const room = grow
                ? bounds[index].max - widths[index]
                : widths[index] - bounds[index].min;
            if (!(room > EPSILON)) {
                continue;
            }
            if (parsed[index].starWeight !== undefined) {
                tier1.push({ index, weight: parsed[index].starWeight as number, room });
            } else {
                tier2.push({ index, weight: Math.max(widths[index], 1), room });
            }
        }

        let candidates: DistributionCandidate[] = tier1.length > 0 ? tier1 : tier2;

        if (candidates.length === 0 && grow) {
            const tier3: DistributionCandidate[] = [];
            for (let index = 0; index < widths.length; index++) {
                if (!eligibleIndexes.has(index)) {
                    continue;
                }
                const weight = parsed[index].starWeight !== undefined
                    ? (parsed[index].starWeight as number)
                    : Math.max(widths[index], 1);
                tier3.push({ index, weight, room: Number.POSITIVE_INFINITY });
            }
            if (tier3.length === 0) {
                break;
            }
            candidates = tier3;
        }

        if (candidates.length === 0) {
            break;
        }

        const totalWeight = candidates.reduce((total, candidate) => total + candidate.weight, 0);
        if (!(totalWeight > 0)) {
            break;
        }

        const magnitude = Math.abs(residual);
        let consumed = 0;
        for (const candidate of candidates) {
            const target = magnitude * (candidate.weight / totalWeight);
            const applied = Math.min(candidate.room, target);
            if (!(applied > 0)) {
                continue;
            }
            if (grow) {
                widths[candidate.index] += applied;
            } else {
                widths[candidate.index] -= applied;
            }
            consumed += applied;
        }

        if (!(consumed > EPSILON)) {
            break;
        }

        residual = grow ? residual - consumed : residual + consumed;
    }

    return widths.map((value) => (Number.isFinite(value) ? Math.max(0, value) : 0));
}

export function resolveColumnWidths(columns: ResizableColumnRegistration[], containerWidth: number): number[] {
    if (columns.length === 0) {
        return [];
    }

    const safeContainerWidth = Number.isFinite(containerWidth) && containerWidth > 0 ? containerWidth : 0;
    const widths = new Array<number>(columns.length).fill(0);

    let fixedTotal = 0;
    let starTotalWeight = 0;
    const parsed = columns.map((column) => parseWidth(column.width));
    for (let index = 0; index < columns.length; index++) {
        if (parsed[index].fixedPx !== undefined) {
            widths[index] = parsed[index].fixedPx as number;
            fixedTotal += widths[index];
        } else {
            starTotalWeight += parsed[index].starWeight || 0;
        }
    }

    const remaining = Math.max(0, safeContainerWidth - fixedTotal);
    for (let index = 0; index < columns.length; index++) {
        if (parsed[index].fixedPx !== undefined) {
            continue;
        }
        const weight = parsed[index].starWeight || 0;
        widths[index] = starTotalWeight > 0 ? (remaining * weight) / starTotalWeight : 0;
    }

    return enforceConstraints(widths, columns, safeContainerWidth);
}

export function enforceConstraints(initialWidths: number[], columns: ResizableColumnRegistration[], containerWidth: number): number[] {
    const eligible = new Set<number>();
    for (let index = 0; index < initialWidths.length; index++) {
        eligible.add(index);
    }
    return distributeResidual(initialWidths, columns, Math.max(0, containerWidth), eligible);
}

export function enforceConstraintsWithPinnedColumn(
    initialWidths: number[],
    columns: ResizableColumnRegistration[],
    containerWidth: number,
    pinnedIndex: number,
): number[] {
    const eligible = new Set<number>();
    for (let index = 0; index < initialWidths.length; index++) {
        if (index !== pinnedIndex) {
            eligible.add(index);
        }
    }

    const targetTotal = Math.max(0, containerWidth);
    const result = distributeResidual(initialWidths, columns, targetTotal, eligible);

    const residual = targetTotal - sum(result);
    if (Math.abs(residual) > EPSILON && pinnedIndex >= 0 && pinnedIndex < columns.length) {
        const bounds = normalizeBounds(columns[pinnedIndex]);
        result[pinnedIndex] = clampNumber(result[pinnedIndex] + residual, bounds.min, bounds.max);
    }
    return result;
}

export function applyDragDelta(
    widths: number[],
    columns: ResizableColumnRegistration[],
    index: number,
    delta: number,
    strategy: ResizeStrategy,
    containerWidth: number,
    closedColumnIds?: Set<string>,
): number[] {
    if (index < 0 || index >= widths.length - 1 || !Number.isFinite(delta)) {
        return widths;
    }

    const isClosed = (columnIndex: number): boolean => {
        const id = columns[columnIndex]?.id;
        return !!id && (closedColumnIds?.has(id) ?? false);
    };

    const next = widths.map((value) => (Number.isFinite(value) ? Math.max(0, value) : 0));
    const leftIndex = index;
    const rightIndexes = strategy === "resize-neighbors"
        ? [index + 1]
        : Array.from({ length: widths.length - index - 1 }, (_, offset) => index + 1 + offset);

    const bounds = columns.map((column) => normalizeBounds(column));

    if (isClosed(leftIndex)) {
        return widths;
    }

    for (const rightIndex of rightIndexes) {
        if (isClosed(rightIndex)) {
            next[rightIndex] = bounds[rightIndex].min;
        }
    }

    const desiredLeft = next[leftIndex] + delta;
    const clampedLeft = clampNumber(desiredLeft, bounds[leftIndex].min, bounds[leftIndex].max);
    next[leftIndex] = clampedLeft;

    const eligibleIndexes = new Set<number>(rightIndexes.filter((rightIndex) => !isClosed(rightIndex)));
    const targetTotal = Math.max(0, containerWidth);
    const result = distributeResidual(next, columns, targetTotal, eligibleIndexes);

    const residual = targetTotal - sum(result);
    if (Math.abs(residual) > EPSILON) {
        result[leftIndex] = clampNumber(result[leftIndex] + residual, bounds[leftIndex].min, bounds[leftIndex].max);
    }

    return result;
}

/**
 * Closed-column reflow: lock every column whose id is in `closedColumnIds` to
 * its `minWidth` (REQ-002), then proportionally redistribute the residual
 * across the remaining non-locked columns. The `triggerColumnId` (when set)
 * is held constant so the user-resized column is preserved during the same
 * adjustment pass (REQ-004).
 *
 * REQ-002 is inviolate: closed columns are always locked at `minWidth`, even
 * if they happen to be the trigger column of the operation that produced
 * `next` (e.g. a separator drag landing on a closed column). The
 * `triggerColumnId` is excluded from the residual sweep only when at least
 * one other non-locked column exists so total width can still be reconciled
 * without starving the only flexible column (REQ-004).
 */
export function preserveClosedColumnWidths(
    next: number[],
    previous: number[],
    columns: ResizableColumnRegistration[],
    closedColumnIds: Set<string>,
    containerWidth: number,
    triggerColumnId?: string,
): number[] {
    if (next.length !== previous.length || next.length !== columns.length) {
        return next;
    }

    const adjusted = next.slice();
    const bounds = columns.map((column) => normalizeBounds(column));
    const lockedIndexes = new Set<number>();
    columns.forEach((column, index) => {
        if (closedColumnIds.has(column.id) && Number.isFinite(previous[index])) {
            adjusted[index] = bounds[index].min;
            lockedIndexes.add(index);
        }
    });

    if (lockedIndexes.size === 0) {
        return next;
    }

    const eligibleIndexes = new Set<number>();
    for (let index = 0; index < adjusted.length; index++) {
        if (lockedIndexes.has(index)) {
            continue;
        }
        eligibleIndexes.add(index);
    }

    if (triggerColumnId) {
        const triggerIndex = columns.findIndex((column) => column.id === triggerColumnId);
        if (triggerIndex >= 0 && eligibleIndexes.size > 1) {
            eligibleIndexes.delete(triggerIndex);
        }
    }

    const targetTotal = Math.max(0, containerWidth);
    return distributeResidual(adjusted, columns, targetTotal, eligibleIndexes);
}

/**
 * Applies a measured width for a toggle-aware column: fallback branch uses the measurement;
 * controlled/single prefers the last separator-drag width when present (memory/restore).
 */
export function applyToggleAwareColumnWidth(
    widths: number[],
    columns: ResizableColumnRegistration[],
    columnIndex: number,
    measuredPx: number,
    branch: ToggleMeasureBranch,
    manualWidths: Record<string, number>,
    containerWidth: number,
): number[] {
    if (columnIndex < 0 || columnIndex >= columns.length) {
        return widths;
    }
    const column = columns[columnIndex];
    if (!column.autoSizeFromToggle) {
        return widths;
    }
    const bounds = normalizeBounds(column);
    const measured = clampNumber(measuredPx, bounds.min, bounds.max);
    let target: number;
    if (branch === "fallback") {
        const previous = widths[columnIndex];
        if (Number.isFinite(previous) && previous > bounds.min) {
            manualWidths[column.id] = clampNumber(previous, bounds.min, bounds.max);
        }
        target = measured;
    } else {
        const manual = manualWidths[column.id];
        target = manual !== undefined ? clampNumber(manual, bounds.min, bounds.max) : measured;
    }
    const next = widths.slice();
    if (Math.abs((next[columnIndex] || 0) - target) < EPSILON) {
        return widths;
    }
    next[columnIndex] = target;
    return enforceConstraintsWithPinnedColumn(next, columns, containerWidth, columnIndex);
}
